import {
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { switchMap } from 'rxjs';
import type { HttpError } from '@models/http-error.model';
import { AppRoutes } from '@helpers/app-routes';
import { CustomerService } from '@services/customer.service';
import { TerminalService } from '@services/terminal.service';
import { AuthService } from '@services/auth.service';
import { SessionService } from '@services/session.service';
import { SessionOrderService } from '@services/session-order.service';
import { CompanyService } from '@services/company.service';
import { MealmomentService } from '@services/meal-moment.service';
import { UserModel } from '@models/user/user.model';
import { OrderCreateModel } from '@models/order/order.model';
import { SessionOrderCreateModel } from '@models/session-order/session-order.model';
import { ModalConfirmationComponent } from '@components/Modals/modal-confirmation/modal-confirmation.component';

@Component({
  selector: 'app-scan-page',
  imports: [FormsModule, TranslateModule, ModalConfirmationComponent],
  templateUrl: './scan-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'block h-full',
  },
})
export class ScanPageComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly customerService = inject(CustomerService);
  private readonly terminalService = inject(TerminalService);
  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(SessionService);
  private readonly sessionOrderService = inject(SessionOrderService);
  private readonly companyService = inject(CompanyService);
  private readonly mealMomentService = inject(MealmomentService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  @ViewChild('searchInput')
  private readonly searchInput?: ElementRef<HTMLInputElement>;
  #autoNavigateTimeout: ReturnType<typeof setTimeout> | null = null;

  searchQuery = '';
  readonly isLoading = signal(false);
  readonly showPassageValidatedModal = signal(false);
  readonly validatedCustomerName = signal('');
  readonly countdownSeconds = 3;

  protected readonly isCheckinMode = computed(
    () => this.terminalService.currentTerminal()?.is_check_in ?? false,
  );

  ngOnInit(): void {
    const terminal = this.terminalService.currentTerminal();
    if (!terminal) {
      return;
    }

    this.terminalService.loadTerminalById(terminal.id).subscribe({
      next: () => this.#initCheckinMode(),
      error: () => {
        this.authService.logout();
        this.toastr.error(
          this.translate.instant('scan_page.errors.checkin_token_error_message'),
          this.translate.instant('scan_page.errors.checkin_token_error_title'),
        );
      },
    });
  }

  #initCheckinMode(): void {
    const terminal = this.terminalService.currentTerminal();
    if (!terminal) {
      return;
    }

    if (!this.isCheckinMode()) {
      return;
    }

    if (this.authService.hasCheckInSession()) {
      this.authService.logout();
      return;
    }

    this.terminalService.checkInToken(terminal.id).subscribe({
      next: (jwt) => {
        this.authService.storeCheckInTokens(jwt.token, jwt.refresh_token);
        this.authService.logout();
      },
      error: () => {
        this.authService.logout();
        this.toastr.error(
          this.translate.instant('scan_page.errors.checkin_token_error_message'),
          this.translate.instant('scan_page.errors.checkin_token_error_title'),
        );
      },
    });
  }

  ngOnDestroy(): void {
    if (this.#autoNavigateTimeout !== null) {
      clearTimeout(this.#autoNavigateTimeout);
    }
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  onSearchInputBlur(event: FocusEvent): void {
    const nextFocusTarget = event.relatedTarget as HTMLElement | null;
    if (nextFocusTarget?.closest('input, textarea, select, [contenteditable="true"]')) {
      return;
    }

    queueMicrotask(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  onPageMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (!target) {
      return;
    }

    if (target.closest('input, textarea, select, [contenteditable="true"]')) {
      return;
    }

    event.preventDefault();
  }

  onSearch(): void {
    if (!this.searchQuery.trim() || this.isLoading()) {
      return;
    }

    this.isLoading.set(true);

    this.customerService.searchCustomer(this.searchQuery.trim()).subscribe({
      next: (customer) => {
        this.isLoading.set(false);

        if (!customer) {
          this.searchQuery = '';
          this.toastr.error(
            this.translate.instant('scan_page.errors.not_found_message'),
            this.translate.instant('scan_page.errors.not_found_title'),
          );
          return;
        }

        if (this.isCheckinMode()) {
          this.#validateReducedPassage(customer);
          return;
        }

        void this.router.navigate([AppRoutes.chooseOrderType]);
      },
      error: (err: HttpError) => {
        this.isLoading.set(false);
        this.searchQuery = '';
        this.toastr.error(
          this.translate.instant('scan_page.' + err.message),
          this.translate.instant('scan_page.errors.search_error_title'),
        );
      },
    });
  }

  onModalButtonClick(): void {
    this.#closePassageValidatedModal();
  }

  #closePassageValidatedModal(): void {
    if (this.#autoNavigateTimeout !== null) {
      clearTimeout(this.#autoNavigateTimeout);
      this.#autoNavigateTimeout = null;
    }
    this.showPassageValidatedModal.set(false);
    queueMicrotask(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  #validateReducedPassage(customer: UserModel): void {
    const terminal = this.terminalService.currentTerminal();
    const company = this.companyService.currentCompany();
    const mealMoment = this.mealMomentService.currentMealMoment();

    if (!terminal || !company || !mealMoment) {
      this.toastr.error(
        this.translate.instant('scan_page.errors.reduced_order_error_message'),
        this.translate.instant('scan_page.errors.reduced_order_error_title'),
      );
      return;
    }

    this.isLoading.set(true);

    this.sessionService
      .getLastSession()
      .pipe(
        switchMap((session) => {
          const order: OrderCreateModel = {
            admission_id: null,
            user_id: customer.id,
            enterprise_id: terminal.enterprise_id,
            mealmoment_id: mealMoment.id,
            company_id: company.id,
            delivery_date: new Date().toISOString().split('T')[0],
            number_of_admission: 1,
            payment_ways: [],
            is_order_for_group: false,
            service_id: null,
            order_items: [],
          };

          const sessionOrder: SessionOrderCreateModel = {
            self_pos_terminal_session_id: session.id,
            user_id: customer.id,
            order,
            is_validated: true,
            is_forced: false,
            forced_comment: null,
          };

          return this.sessionOrderService.createSessionOrder(sessionOrder);
        }),
      )
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          this.searchQuery = '';
          this.validatedCustomerName.set(this.#getCustomerDisplayName(customer));
          this.showPassageValidatedModal.set(true);
          this.#autoNavigateTimeout = setTimeout(
            () => this.#closePassageValidatedModal(),
            this.countdownSeconds * 1000,
          );
        },
        error: () => {
          this.isLoading.set(false);
          this.toastr.error(
            this.translate.instant('scan_page.errors.reduced_order_error_message'),
            this.translate.instant('scan_page.errors.reduced_order_error_title'),
          );
        },
      });
  }

  #getCustomerDisplayName(customer: UserModel): string {
    const fullName = `${customer.firstname ?? ''} ${customer.lastname ?? ''}`.trim();
    return fullName || customer.username;
  }
}
